import React, { useState } from "react";

import { useMutation } from "@tanstack/react-query";
import { noop } from "lodash/fp";
import * as yup from "yup";

import * as Events from "@app/analytics/tracking/events";
import { AddReadUILocation } from "@app/analytics/types/trackingEventTypes";
import apiClient from "@app/common/api/apiClient";
import { parseJoinInfo } from "@app/common/utils/parsingUtils";

const urlValidationSchema = yup.object().shape({
  meetingUrl: yup.string().url(),
});

interface UseAddReadOptions {
  uiLocation: AddReadUILocation;
  onValidationFail: () => void;
}

const DEFAULT_OPTIONS: UseAddReadOptions = {
  uiLocation: "unset",
  onValidationFail: noop,
};

const useAddRead = (optsParam?: Partial<UseAddReadOptions>) => {
  const opts = {
    ...DEFAULT_OPTIONS,
    ...optsParam,
  };

  const [meetingUrl, setMeetingUrl] = useState("");
  const [meetingUrlError, setMeetingUrlError] = useState(false);

  const createSessionMutation = useMutation({
    mutationFn: apiClient.session.createSession,
    onSuccess: () =>
      Events.addReadToLiveMeeting({ uiLocation: opts.uiLocation }),
  });

  const validateUrl = (url: string, silent = false) => {
    const isValid = urlValidationSchema.isValidSync({ meetingUrl: url });
    setMeetingUrlError(!isValid);
    if (!isValid && !silent) {
      opts.onValidationFail();
    }
    return isValid;
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const url = parseJoinInfo(event.target.value);
    setMeetingUrl(url);
    if (url && meetingUrlError) {
      validateUrl(url, true);
    } else if (!url) {
      setMeetingUrlError(false);
    }
  };

  const reset = () => {
    setMeetingUrlError(false);
    setMeetingUrl("");
  };

  return {
    addRead: createSessionMutation,
    hasInputError: meetingUrlError,
    meetingUrl,
    onInputChange: handleInputChange,
    reset,
    validate: validateUrl,
  };
};

export default useAddRead;
